import { API } from "aws-amplify";

export type ProgramMemberFilterOption = {
  userId: string;
  label: string;
};

/** Dedupe by userId; attendings win over trainees when both exist (same label kept from first occurrence in argument order). */
export function mergeDistinctProgramMemberOptions(
  ...groups: ProgramMemberFilterOption[][]
): ProgramMemberFilterOption[] {
  const byId = new Map<string, ProgramMemberFilterOption>();
  for (const group of groups) {
    for (const opt of group) {
      if (!byId.has(opt.userId)) {
        byId.set(opt.userId, opt);
      }
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * GET program/{id} returns linked networks. When any network has traineeCanRateTrainee, trainees may appear as evaluators in filters.
 */
export async function fetchProgramAllowsTraineeEvaluators(programId: string): Promise<boolean> {
  try {
    const payload = (await API.get("simpl-api", `program/${programId}`, {})) as {
      error?: string;
      networks?: Array<{ traineeCanRateTrainee?: boolean | null } | null> | null;
    };
    if (payload && typeof payload === "object" && payload.error) {
      return false;
    }
    for (const n of payload?.networks || []) {
      if (n?.traineeCanRateTrainee === true) {
        return true;
      }
    }
  } catch {
    return false;
  }
  return false;
}

export type ActivityFilterOption = {
  id: string;
  title: string;
};

function membershipRowToOption(row: {
  programMember?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null;
  programMemberId?: string | null;
  programRole?: string | null;
}): ProgramMemberFilterOption | null {
  const userId = row.programMember?.id || row.programMemberId;
  if (!userId) {
    return null;
  }
  const first = row.programMember?.firstName || "";
  const last = row.programMember?.lastName || "";
  const name = `${first} ${last}`.trim() || "Unknown";
  const email = row.programMember?.email?.trim();
  const label = email ? `${name} (${email})` : name;
  return { userId, label };
}

/**
 * Program roster for filter pickers. Backed by `program-memberships/search` (not the assessments list API).
 */
export async function fetchProgramMembersForAssessmentFilters(
  programId: string
): Promise<{ trainees: ProgramMemberFilterOption[]; attendings: ProgramMemberFilterOption[] }> {
  const resp = (await API.get("simpl-api", "program-memberships/search", {
    queryStringParameters: {
      filter: JSON.stringify({
        programId: { eq: programId },
        endDate: { activeOnly: true }
      })
    }
  })) as { programmemberships?: unknown[] };

  const rows = resp.programmemberships || [];
  const trainees: ProgramMemberFilterOption[] = [];
  const attendings: ProgramMemberFilterOption[] = [];

  for (const raw of rows) {
    const row = raw as Parameters<typeof membershipRowToOption>[0];
    const opt = membershipRowToOption(row);
    if (!opt) {
      continue;
    }
    const role = String(row.programRole || "").toLowerCase();
    if (role.includes("trainee")) {
      trainees.push(opt);
    } else if (role.includes("attending")) {
      attendings.push(opt);
    }
  }

  const byLabel = (a: ProgramMemberFilterOption, b: ProgramMemberFilterOption) =>
    a.label.localeCompare(b.label);

  trainees.sort(byLabel);
  attendings.sort(byLabel);

  return { trainees, attendings };
}

const ACTIVITY_SEARCH_MIN_CHARS = 2;

/**
 * Activity title search for filter pickers. Backed by `activities/search` (not the assessments list API).
 */
export async function searchActivitiesForAssessmentFilter(input: {
  titleContains: string;
  sponsorId?: string;
}): Promise<ActivityFilterOption[]> {
  const q = input.titleContains.trim();
  if (q.length < ACTIVITY_SEARCH_MIN_CHARS) {
    return [];
  }
  const queryStringParameters: Record<string, string> = { title: q };
  if (input.sponsorId?.trim()) {
    queryStringParameters.sponsorId = input.sponsorId.trim();
  }
  const resp = (await API.get("simpl-api", "activities/search", {
    queryStringParameters
  })) as {
    activities?: { id: string; title?: string | null }[];
  };

  return (resp.activities || []).map((a) => ({
    id: a.id,
    title: (a.title && a.title.trim()) || a.id
  }));
}
